import { lazy, Suspense, useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useLoggingQuery } from 'lib/apollo/apollo-hooks';
import { getLocationParams } from 'lib/router/router.util';
import { ContentPageSection } from 'approot/shared/content-page/content-page-section-title';
import { getIdFromSlug } from 'lib/strings/strings';
import { ContentPageHeader } from 'approot/shared/content-page/content-page-header';
import { getContentUnit } from 'approot/shared/content-page/content-page.utils';
import { LESSON, ENGLISH, MATHS, LESSON_ACTIVITIES } from 'global/constants';
import {
  OUTCOMES_HASH,
  PERFORMANCE_EXPECTATIONS_HASH,
  VOCABULARIES_HASH,
  FOCUS_QUESTION_HASH,
  PHENOMENA_HASH,
  SEQUENCE_HASH,
  TEXTUAL_CONCEPTS_HASH,
  BIG_IDEAS_HASH,
  MATERIALS_HASH,
} from 'webclient.constants';
import { useAuthorisedAction } from 'approot/shared/hooks/authorised-action.hooks';
import {
  hasNotes,
  hasVocabulary,
  hasPrintables,
  hasWorksheet,
  hasFocusOrPhenomenaQuestion,
} from './lesson.utils';
import { convertRichtextToPlainText } from 'lib/form/richtextarea/richtextarea.utils';
import { isFailedToFetch } from 'approot/errors/error.utils';
import { setGlobalModal } from 'approot/global-notifications/global-modals.apollo';
import ContentPageNav from 'approot/shared/content-page/content-page-nav';
import ContentPageActionDropdown from 'approot/shared/content-page/content-page-action-dropdown';
import ReadMoreVocab from 'approot/shared/read-more/read-more-vocab';
import { spacing16, spacing8 } from 'approot/shared/styles/spacing.style';
import { hasSequenceContent } from 'approot/shared/teaching-learning-sequence/teaching-learning-sequence.utils';
import { getUnitProgramLabel } from 'approot/shared/logic/content-label';
import {
  ContentPageContentLayout,
  ContentPageHeaderLayout,
} from 'approot/shared/content-page/content-page.style';
import { addRegionalSuffix } from 'approot/shared/helmet/helmet';
import { NAMESPACE_TRANSLATION } from 'internationalise';
import { LessonMaterial } from 'approot/unit/sections/lesson-materials/lesson-material-items';
import {
  LESSON_TABS,
  PREVIOUS,
  PRINTABLES,
  STIMULUS,
} from './lesson.constants';
import ReadMoreRichText from 'approot/shared/read-more/read-more-richtext';
import { useRegionData } from 'approot/shared/region/region.apollo';
import { useGetLessonSequences } from 'approot/activity-builder/components/teaching-notes/teaching-notes.hooks';
import { splitPreviousAndCurrentStimulus } from 'approot/shared/stimulus/stimulus.utils';
import { hasPublishedActivities } from 'approot/shared/logic/has-published-activities';
import TeachingLearningSequenceGuest from './sections/teaching-learning-sequence/teaching-learning-sequence-guest';
import TextualConceptsView from './sections/textual-concepts/textual-concepts-view';
import BigIdeasView from './sections/big-ideas/big-ideas-view';
import StimulusGuest from 'approot/shared/stimulus/stimulus-guest';
import LessonActionBar from './lesson-action-bar';
import LessonPrintablesGuest from './sections/lesson-printables/lesson-printables-guest';
import LessonActivitiesPreviewGuest from './lesson-activities-preview-guest';
import LessonWorksheetPreviewGuest from './lesson-worksheet-preview-guest';
import LessonWorksheetsGuest from './sections/lesson-worksheets/lesson-worksheets-guest';
import LessonVocabularyView from './sections/vocabulary/vocabulary-view';
import { LessonColFixed, LessonCol, LessonRow } from './lesson.style';
import { GET_GUEST_LESSON_DETAILS } from './lesson-guest.graphql';
import {
  GetGuestLessonById,
  GetGuestLessonByIdVariables,
} from './__generated__/GetGuestLessonById';
import { useGetActivityMaterials } from 'approot/unit/sections/lesson-materials/lesson-material.hooks';
import LessonMaterialsCalculator from 'approot/shared/lesson-materials/lesson-materials-calculator';
import BadgeActivityMediaType from 'approot/shared/badge/badge-activity-media-type';
import { useGetPublishedLessonActivities } from 'approot/activity-builder/graphql/get-published-lesson-activities.hooks';
import ActivitiesPreviewSkeleton from './activities-preview-skeleton';
import ContentPageArchivedBanner from 'approot/shared/content-page/content-page-archived-banner';
import FocusQuestionView from './sections/focus-question/focus-question-view';
import LayoutGuest from 'approot/shared/layout/layout-guest';

const PerformanceExpectations = lazy(() =>
  import('approot/shared/outcomes/performance-expectations')
);
const Outcomes = lazy(() => import('approot/shared/outcomes/outcomes'));

const LessonGuest = () => {
  const { t } = useTranslation(NAMESPACE_TRANSLATION);
  const { region } = useRegionData();

  const { slug: routeSlug = '' } = useParams<{ slug: string }>();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState<string>(LESSON_TABS[0].value);

  const {
    authoriseActiveUser,
    unauthorisedAccessModal,
  } = useAuthorisedAction();

  const lessonId = getIdFromSlug(routeSlug);

  const {
    data: lessonData,
    loading: lessonLoading,
    error: lessonError,
  } = useLoggingQuery<GetGuestLessonById, GetGuestLessonByIdVariables>(
    GET_GUEST_LESSON_DETAILS,
    {
      variables: {
        input: {
          id: lessonId!,
        },
      },
      skip: !lessonId,
      onError: error => {
        if (isFailedToFetch(error)) {
          throw error; // will be caught by apollo error boundary
        }
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
    false
  );

  const lesson = lessonData?.getLessonById;
  const displayActivities = hasPublishedActivities(lesson?.lessonActivities);

  const {
    data: publishedActivitiesData,
    loading: publishedActivitiesLoading,
  } = useGetPublishedLessonActivities({
    version: lesson?.lessonActivities?.[0]?.version || '',
    skip: !displayActivities,
    isSignedIn: false,
  });

  const activitiesData =
    publishedActivitiesData?.getPublishedLessonActivitiesByVersion;

  const { activitiesMaterials } = useGetActivityMaterials({ lessonId });

  const [unit] = getContentUnit(lesson, getLocationParams(location));

  const { currentStimulus, previousStimulus } = splitPreviousAndCurrentStimulus(
    {
      stimulusResources: lesson?.stimulusResources || [],
      parentType: LESSON,
      hasActivities: !!displayActivities,
    }
  );

  const displayStimulusTab = currentStimulus.length > 0;

  const {
    sequences: activitySequences,
    loading: activitySequencesLoading,
  } = useGetLessonSequences({
    contentId: activitiesData?.id || 0,
    version: activitiesData?.version || '',
    skip: !displayActivities || !activitiesData,
    fetchPolicy: 'cache-and-network',
    authenticate: false,
  });

  useEffect(() => {
    setActiveTab('lesson');
  }, [lessonId]);

  return (
    <LayoutGuest
      withFooter={
        !lessonLoading &&
        !publishedActivitiesLoading &&
        !activitySequencesLoading
      }
      loading={lessonLoading}
      error={lessonError}
      errorNotFound={!lessonId}
      pageMeta={
        lesson
          ? {
              name: addRegionalSuffix(
                `${lesson.name} Lesson | ${lesson.subjects[0].label}`,
                region
              ),
              description: convertRichtextToPlainText(lesson.notesJson),
              responsiveImages: {
                landscape: lesson.landscapeImage,
                square: lesson.squareImage,
              },
            }
          : undefined
      }
      page={() => {
        if (!lesson || !unit) return null;

        const {
          id,
          notesJson,
          subjects,
          data,
          outcomes,
          performanceExpectations,
          materials,
          lessonActivitiesSummary,
        } = lesson;

        const teachingAndLearningSequence =
          activitySequences || lesson.teachingAndLearningSequence;

        //Backend supports multiple subjects for a topic but frontend doesn't. So we'll just pick the first one.
        const subject = subjects[0];
        const subjectName = (subject && subject.name) || ' ';
        const unitProgramLabel = getUnitProgramLabel(t);

        return (
          <>
            {lesson.archivedAt && (
              <ContentPageArchivedBanner slug={unit.slug} />
            )}
            <ContentPageHeaderLayout>
              <ContentPageHeader
                title={lesson.name}
                breadcrumbSubject={unit.subjects[0]}
                subject={subject}
                years={lesson.years}
                topic={{ name: unit.topic.label, slug: unit.topic.slug }}
                unit={unit}
                item={{ name: lesson.name, slug: lesson.slug }}
                englishContentArea={lesson.data?.englishContentArea}
                badge={
                  <BadgeActivityMediaType
                    hasActivities={displayActivities}
                    showTooltip
                  />
                }
                navigation={
                  <ContentPageNav
                    item={{
                      id: lesson.id,
                      label: 'Lesson',
                      title: lesson.archivedAt ? 'Archived Lesson' : undefined,
                      __typename: lesson.__typename,
                    }}
                    otherItems={[...unit.lessons, ...unit.assessments]}
                    unitId={unit.id}
                  />
                }
                performanceExpectations={performanceExpectations}
                archived={!!lesson.archivedAt}
              />
            </ContentPageHeaderLayout>

            <LessonActionBar
              activeTab={activeTab}
              showPrintables={
                !!(
                  hasPrintables(lesson, !displayActivities) ||
                  activitiesData?.studentPrintables
                )
              }
              showStimulus={displayStimulusTab}
              showPreviousVersion={displayActivities && hasWorksheet(lesson)}
              actionButtons={
                <ContentPageActionDropdown
                  dropdownOptions={[
                    {
                      text: `Download ${unitProgramLabel}`,
                      onClick: () =>
                        authoriseActiveUser(() =>
                          setGlobalModal(unauthorisedAccessModal)
                        ),
                    },
                  ]}
                />
              }
              onChangeTab={setActiveTab}
              lessonId={lesson.id}
            />

            <ContentPageContentLayout id="lesson-content" tabIndex={-1}>
              {activeTab === LESSON_TABS[0].value && (
                <LessonRow>
                  <LessonColFixed data-selector="lesson-content-section">
                    {lessonLoading || publishedActivitiesLoading ? (
                      <ActivitiesPreviewSkeleton />
                    ) : displayActivities && activitiesData ? (
                      <LessonActivitiesPreviewGuest
                        contentId={id}
                        contentType={LESSON_ACTIVITIES}
                        authorId={lesson.authorId}
                        slug={routeSlug}
                        unitName={unit.name}
                        subjectName={subject.label}
                        contentName={lesson.name}
                        overview={activitiesData.sectionsOverview}
                        baseFontSize={activitiesData.styles?.fontSize}
                        focusQuestion={lesson.focusQuestion}
                        phenomena={lesson.phenomena}
                        landscapeImage={activitiesData.landscapeImage}
                        version={activitiesData.version}
                      />
                    ) : (
                      !displayActivities &&
                      hasWorksheet(lesson) && (
                        <LessonWorksheetPreviewGuest
                          contentId={id}
                          worksheet={lesson.worksheet}
                        />
                      )
                    )}
                  </LessonColFixed>
                  <LessonCol>
                    {hasNotes(lesson) && (
                      <ContentPageSection
                        title="Lesson Overview"
                        hash="lesson-overview"
                        isFullWidth
                      >
                        <ReadMoreRichText
                          title="Lesson Overview"
                          content={notesJson}
                        />
                      </ContentPageSection>
                    )}

                    {!displayActivities && materials && materials?.length > 0 && (
                      <LessonMaterial
                        lessonMaterials={{
                          id: lesson.id,
                          materials: materials,
                        }}
                        render={content => (
                          <ContentPageSection
                            title="Lesson Materials"
                            hash={MATERIALS_HASH}
                            isFullWidth
                          >
                            {content}
                          </ContentPageSection>
                        )}
                      />
                    )}

                    {displayActivities && !!activitiesMaterials?.length && (
                      <LessonMaterialsCalculator
                        render={content => (
                          <ContentPageSection
                            title="Lesson Materials Calculator"
                            hash={MATERIALS_HASH}
                            titleMarginBottom={spacing8}
                            isFullWidth
                            marginBottom={0}
                          >
                            {content}
                          </ContentPageSection>
                        )}
                        notes={activitiesMaterials}
                      />
                    )}

                    {outcomes && outcomes?.length > 0 && (
                      <ContentPageSection
                        title="Outcomes Covered"
                        hash={OUTCOMES_HASH}
                        titleMarginBottom={4}
                        isFullWidth
                      >
                        <Suspense fallback={<div />}>
                          <Outcomes
                            outcomes={outcomes}
                            contentTypeLabel="Lesson"
                            contentType={LESSON}
                          />
                        </Suspense>
                      </ContentPageSection>
                    )}

                    {performanceExpectations &&
                      performanceExpectations?.length > 0 && (
                        <ContentPageSection
                          title="Standards"
                          hash={PERFORMANCE_EXPECTATIONS_HASH}
                          titleMarginBottom={spacing16}
                          isFullWidth
                        >
                          <Suspense fallback={<div />}>
                            <PerformanceExpectations
                              outcomes={performanceExpectations}
                            />
                          </Suspense>
                        </ContentPageSection>
                      )}

                    {hasFocusOrPhenomenaQuestion({
                      isActivitiesPublished: displayActivities,
                      question: lesson.focusQuestion,
                      activitiesQuestion: activitiesData?.focusQuestion,
                    }) && (
                      <ContentPageSection
                        title="Focus Question"
                        hash={FOCUS_QUESTION_HASH}
                        isFullWidth
                      >
                        <FocusQuestionView
                          question={lesson.focusQuestion}
                          activitiesQuestion={activitiesData?.focusQuestion}
                          contentId={lesson.id}
                        />
                      </ContentPageSection>
                    )}

                    {hasFocusOrPhenomenaQuestion({
                      isActivitiesPublished: displayActivities,
                      question: lesson.phenomena,
                      activitiesQuestion: activitiesData?.phenomena,
                    }) && (
                      <ContentPageSection
                        title="Phenomena Question"
                        hash={PHENOMENA_HASH}
                        isFullWidth
                      >
                        <FocusQuestionView
                          question={lesson.phenomena}
                          activitiesQuestion={activitiesData?.phenomena}
                          contentId={lesson.id}
                        />
                      </ContentPageSection>
                    )}

                    {hasVocabulary(lesson.data) && (
                      <ContentPageSection
                        title="Lesson Vocabulary"
                        hash={VOCABULARIES_HASH}
                        isFullWidth
                      >
                        <LessonVocabularyView
                          vocabulary={data && data.vocabularies}
                          note={
                            <ReadMoreVocab
                              contentType="Lesson Vocabulary"
                              subject={subjectName}
                            />
                          }
                        />
                      </ContentPageSection>
                    )}

                    {subjectName === ENGLISH &&
                      lesson.textualConcepts &&
                      lesson.textualConcepts.length > 0 && (
                        <ContentPageSection
                          title="Textual Concepts"
                          hash={TEXTUAL_CONCEPTS_HASH}
                          isFullWidth
                        >
                          <TextualConceptsView
                            content={{
                              id: lesson.id,
                              textualConcepts: lesson.textualConcepts,
                              __typename: 'Lesson',
                            }}
                          />
                        </ContentPageSection>
                      )}

                    {subjectName === MATHS &&
                      lesson.bigIdeas &&
                      lesson.bigIdeas.length > 0 && (
                        <ContentPageSection
                          title="Big Ideas"
                          hash={BIG_IDEAS_HASH}
                          isFullWidth
                        >
                          <BigIdeasView
                            content={{
                              id: lesson.id,
                              bigIdeas: lesson.bigIdeas,
                              __typename: 'Lesson',
                            }}
                          />
                        </ContentPageSection>
                      )}

                    {hasSequenceContent(teachingAndLearningSequence) && (
                      <ContentPageSection
                        title="Teaching and Learning Sequence"
                        hash={SEQUENCE_HASH}
                        titleMarginBottom={spacing16}
                        isFullWidth
                      >
                        <TeachingLearningSequenceGuest
                          sequences={teachingAndLearningSequence}
                          activitySequences={activitySequences}
                          sectionsOverview={activitiesData?.sectionsOverview}
                          isAccountExpired={false}
                          totalGreenActivities={
                            lessonActivitiesSummary?.coreActivities || 0
                          }
                          totalBlueActivities={
                            lessonActivitiesSummary?.deeperActivities || 0
                          }
                          totalBlackActivities={
                            lessonActivitiesSummary?.challengingActivities || 0
                          }
                          totalStimulus={lessonActivitiesSummary?.stimulus || 0}
                          contentId={lesson.id}
                          contentType={LESSON}
                        />
                      </ContentPageSection>
                    )}
                  </LessonCol>
                </LessonRow>
              )}

              {activeTab === PRINTABLES && (
                <LessonPrintablesGuest
                  sheets={lesson}
                  studentPrintables={
                    activitiesData ? activitiesData.studentPrintables : null
                  }
                  displayAnswerSheet={!displayActivities}
                />
              )}

              {activeTab === STIMULUS && (
                <div data-selector="stimulus-content-section">
                  <StimulusGuest
                    stimulus={{
                      id: lesson.id,
                      stimulusResources: currentStimulus,
                    }}
                  />
                </div>
              )}

              {displayActivities && activeTab === PREVIOUS && (
                <div data-selector="previous-content-section">
                  <LessonWorksheetsGuest
                    contentId={id}
                    lesson={lesson}
                    stimulus={previousStimulus}
                    showLessonTab={() => setActiveTab(LESSON_TABS[0].value)}
                    hasActivities
                    printableAnswerSheet={lesson.printableAnswerSheet}
                  />
                </div>
              )}
            </ContentPageContentLayout>
          </>
        );
      }}
    />
  );
};

export default LessonGuest;
